import { useCallback, useContext, useMemo } from "react";
import { ProjectContext } from "context/ProjectProvider";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  mapLodEnumValuesToRows,
  toLodUpdateEnumValues,
  type DesignScopeLodEnumValue,
  type DesignScopeLodRow,
  type DesignScopeLodScope
} from "./model";
import {
  GET_PROJECT_DESIGN_SCOPE_LEVEL_OF_DETAIL,
  GET_SUBSCRIPTION_DESIGN_SCOPE_LEVEL_OF_DETAIL,
  UPDATE_PROJECT_DESIGN_SCOPE_LEVEL_OF_DETAIL,
  UPDATE_SUBSCRIPTION_DESIGN_SCOPE_LEVEL_OF_DETAIL
} from "./queries";

type LodApiPayload = {
  enum_values?: DesignScopeLodEnumValue[] | null;
};

type ProjectLodQueryData = {
  get_project_design_scope_level_of_detail?: LodApiPayload | null;
};

type SubscriptionLodQueryData = {
  get_subscription_design_scope_level_of_detail?: LodApiPayload | null;
};

export function useDesignScopeLodSettings(scope: DesignScopeLodScope) {
  const { gqlClientForProject } = useContext(ProjectContext);
  const isProject = scope === "project";

  const projectQuery = useCIQQuery<ProjectLodQueryData>(
    GET_PROJECT_DESIGN_SCOPE_LEVEL_OF_DETAIL,
    {
      client: gqlClientForProject,
      skip: !isProject || !gqlClientForProject
    }
  );

  const subscriptionQuery = useCIQQuery<SubscriptionLodQueryData>(
    GET_SUBSCRIPTION_DESIGN_SCOPE_LEVEL_OF_DETAIL,
    {
      skip: isProject
    }
  );

  const [updateProjectLod] = useCIQMutation(
    UPDATE_PROJECT_DESIGN_SCOPE_LEVEL_OF_DETAIL,
    gqlClientForProject ? { client: gqlClientForProject } : {}
  );
  const [updateSubscriptionLod] = useCIQMutation(
    UPDATE_SUBSCRIPTION_DESIGN_SCOPE_LEVEL_OF_DETAIL
  );

  const query = isProject ? projectQuery : subscriptionQuery;
  const refetch = query.refetch;
  const enumValues = isProject
    ? projectQuery.data?.get_project_design_scope_level_of_detail?.enum_values
    : subscriptionQuery.data?.get_subscription_design_scope_level_of_detail
        ?.enum_values;

  const mapped = useMemo(
    () => mapLodEnumValuesToRows(enumValues),
    [enumValues]
  );

  const save = useCallback(
    async (rows: DesignScopeLodRow[], defaultId: string) => {
      const mutate = isProject ? updateProjectLod : updateSubscriptionLod;
      const result = await mutate({
        variables: {
          enum_values: toLodUpdateEnumValues(rows, defaultId)
        }
      });

      if (!result?.success) return { success: false as const, message: "" };

      const message = isProject
        ? result.data?.update_project_design_scope_level_of_detail?.message
        : result.data?.update_subscription_design_scope_level_of_detail
            ?.message;

      await refetch?.();
      return {
        success: true as const,
        message: typeof message === "string" ? message : ""
      };
    },
    [isProject, refetch, updateProjectLod, updateSubscriptionLod]
  );

  return {
    rows: mapped.rows,
    defaultId: mapped.defaultId,
    loading: query.loading,
    save
  };
}
